// GlobalSnackbarProvider.tsx
import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from "react"
import { Snackbar, Alert, AlertColor } from "@mui/material"

type SnackbarPosition = "top" | "bottom" | "center"

interface SnackbarOptions {
  position?: SnackbarPosition
}

interface SnackbarContextProps {
  showSnackbar: (message: string | any, severity?: AlertColor, options?: SnackbarOptions) => void
}

const SnackbarContext = createContext<SnackbarContextProps | undefined>(undefined)

export const GlobalSnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState("")
  const [severity, setSeverity] = useState<AlertColor>("info")
  const [position, setPosition] = useState<SnackbarPosition>("bottom")

  const showSnackbar = useCallback((msg: string | any, sev: AlertColor = "info", options?: SnackbarOptions) => {
    // 안전한 메시지 처리
    let safeMessage: string

    if (typeof msg === "string") {
      safeMessage = msg
    } else if (typeof msg === "object" && msg !== null) {
      // 객체인 경우 첫 번째 문자열 값 추출 시도
      const values = Object.values(msg)
      const firstStringValue = values.find(value => typeof value === "string")
      safeMessage = (firstStringValue as string) || "오류가 발생했습니다."
    } else {
      // 기타 타입인 경우 문자열로 변환
      safeMessage = String(msg) || "오류가 발생했습니다."
    }

    setMessage(safeMessage)
    setSeverity(sev)
    setPosition(options?.position ?? "bottom")
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const snackbarContextValue = useMemo(() => ({ showSnackbar }), [showSnackbar])
  const isCenterPosition = position === "center"

  return (
    <SnackbarContext.Provider value={snackbarContextValue}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: isCenterPosition ? "top" : position, horizontal: "center" }}
        sx={
          isCenterPosition
            ? {
                top: "50% !important",
                bottom: "auto !important",
                left: "50% !important",
                right: "auto !important",
                transform: "translate(-50%, -50%)",
              }
            : position === "bottom"
              ? { bottom: "calc(76px + env(safe-area-inset-bottom, 0px)) !important" }
              : undefined
        }
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  )
}

export const useGlobalSnackbar = (): SnackbarContextProps => {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error("useGlobalSnackbar must be used within a GlobalSnackbarProvider")
  }
  return context
}
